import React, { useState } from 'react';
import * as Constants from '../../shared/Constants';
import { useUser, userManager } from '../../logic/imports';

/**
 * A collapsible box with beta notes and version updates.
 * Shows title when collapsed, full content when expanded.
 */
export const BetaNotes: React.FC = () => {
    const [isExpanded, setIsExpanded] = useState(false);

    // Determine if the user has already seen the latest version notes
    const { userData } = useUser();
    const userDataLoaded = userData.userId !== '';
    const badgeWasDismissed = userData.lastVersionBadgeDismissed === Constants.VERSION;
    const shouldShowBadge = userDataLoaded && !badgeWasDismissed;

    const handleExpand = () => {
        // Dismiss badge by setting lastVersionBadgeDismissed to current version
        userManager.data.lastVersionBadgeDismissed = Constants.VERSION;
        userManager.onUserUpdated?.(userManager.data, userManager.prefs);
        userManager.saveUserData({ userData: true }).catch(err => console.error('Failed to save badge dismissal:', err));
        setIsExpanded(!isExpanded);
    };

    const version = (
        <span style={{ marginTop: '5px', fontSize: '12px', fontStyle: 'italic' }}>
            version {Constants.VERSION} , {Constants.VERSION_DATE}
        </span>
    )

    const content = (
        <div style={{ marginTop: '10px', textAlign: 'left', padding: '0 10px' }}>
            Please keep the following in mind while testing:
            <ul style={{ marginTop: '5px' }}>
                <li>This is a beta version of the app. Features may be incomplete or unstable.</li>
                <li>Keep your brokerage's trading app handy, or the website bookmarked, in case this app encounters issues and you need to exit a position.</li>
                <li>Orders created outside of this app will not appear here.</li>
                <li>Orders created here will <i>not</i> appear in your brokerage's app. They are managed by this app until triggered, at which point they submit a MARKET order.</li>
                <li>The Flatten-Position and Cancel-Order buttons will execute <i>immediately</i> without asking for confirmation.</li>
            </ul>
            <hr/><br/>
            Known Bugs:
            <ul style={{ marginTop: '5px' }}>
                <li>Account Value may not properly update during trading and may need a page refresh.</li>
            </ul>
            <hr/><br/>
            If you encounter any issues, please screenshot and report it!<br/><br/>
            Thank you for helping with the development of this app, it's truly appreciated!
        </div>
    )

    return (
        <div 
            className="message-box" 
            style={{
                cursor: 'pointer',
                userSelect: 'none',
                display: 'flex',
                flexDirection: 'column',
                width: '95%',
                margin: '10px auto',
                position: 'relative'
            }}
            onClick={() => handleExpand()}
        >
            {/* New update badge */}
            {shouldShowBadge && (
                <>
                    <style>{`
                        @keyframes pulsate {
                            0%, 100% { transform: scale(1); }
                            50% { transform: scale(1.12); }
                        }
                    `}</style>
                    <div 
                        style={{
                            position: 'absolute',
                            top: '8px',
                            right: '32px',
                            backgroundColor: '#ff6b6b',
                            color: 'white',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontSize: '11px',
                            fontWeight: 'bold',
                            whiteSpace: 'nowrap',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                            cursor: 'pointer',
                            animation: 'pulsate 2s ease-in-out infinite',
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                            handleExpand();
                        }}
                    >
                        New update!
                    </div>
                </>
            )}
            {/* Title */}
            <h3>Beta Testing Notes</h3>

            {/* Version */}
            {isExpanded && version}

            {/* Content */}
            {isExpanded && content}

            {/* Collapse/Expand icon */}
            <span style={{ 
                fontSize: '12px', 
                flexShrink: 0,
                position: 'absolute',
                top: '8px',
                right: '8px'
            }}>
                {isExpanded ? '▼' : '▶'}
            </span>
        </div>
    );
};
